import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UUID } from "@/api/types";
import { isApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FolderPicker } from "@/components/folder/FolderPicker";

/**
 * Generic «Перенести в другую папку» dialog (folder-management-v3 §7).
 *
 * Two consumers wire different mutations into the same shell:
 *   * `CardHeaderBlock` → `moveMeeting(meetingId, folderId)`
 *   * `ClientFormDialog`   (edit-mode, admin only) → `moveClient(...)`
 *
 * Employees no longer use this dialog (TM-5d6213f1) — folder membership
 * for an Employee is multi-folder and is mutated via PATCH `folder_ids`
 * inside `EmployeeFormDialog`.
 *
 * The dialog itself is dumb: it renders the picker, fires `onMove(id)`
 * on confirm, and translates the standard `RecognitionErrorEnvelope`
 * codes (`forbidden` / `not_found` / `validation_error`) into Russian
 * inline error toasts. The caller is responsible for the actual API call
 * and for the react-query invalidation that follows on success.
 */
export interface MoveFolderDialogProps {
  open: boolean;
  /** The resource's current folder; used to pre-select the picker. */
  currentFolderId: UUID | null;
  /** Fired with the chosen folder id; should resolve on success, reject on error. */
  onMove: (folderId: UUID) => Promise<void>;
  onClose: () => void;
  /** Optional override for the title (defaults to «Перенести в другую папку»). */
  title?: string;
}

export function MoveFolderDialog({
  open,
  currentFolderId,
  onMove,
  onClose,
  title,
}: MoveFolderDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<UUID | null>(currentFolderId);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(currentFolderId);
    setError(null);
    setPending(false);
  }, [open, currentFolderId]);

  async function handleConfirm(): Promise<void> {
    if (selected === null) return;
    setPending(true);
    setError(null);
    try {
      await onMove(selected);
      onClose();
    } catch (err) {
      setError(messageForError(err, t));
    } finally {
      setPending(false);
    }
  }

  const isUnchanged = selected === currentFolderId;
  const disabled = pending || selected === null || isUnchanged;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title ?? t("folders.move.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FolderPicker
            value={selected}
            onChange={(id) => {
              setSelected(id);
              setError(null);
            }}
            disabled={pending}
          />
          {error !== null ? (
            <p
              role="alert"
              data-testid="move-folder-error"
              className="text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="move-folder-confirm"
            disabled={disabled}
            onClick={handleConfirm}
          >
            {pending ? t("common.saving") : t("folders.move.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function messageForError(err: unknown, t: (k: string) => string): string {
  if (isApiError(err)) {
    if (err.code === "forbidden") return t("folders.move.errors.forbidden");
    if (err.code === "not_found") return t("folders.move.errors.notFound");
    if (err.code === "validation_error") {
      return t("folders.move.errors.validation");
    }
  }
  return t("folders.move.errors.generic");
}
